/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, OnDestroy } from '@angular/core';

import moment from 'moment-timezone';
import { includes } from 'underscore';
import { of , Observable, Subscription, tap, filter, BehaviorSubject } from 'rxjs';

import { MentionsSearchResponseModel } from '../../../api/mentions/model/mentions-search-response.model';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { ReportBlockTypeEnum } from '../enums/report-block-type.enum';
import { DataPointModel } from '../../Charts/models/data-point.model';
import { MentionSearchFacetTypesEnum } from '../../../api/mentions/enum/mention-search-facet-types.enum';
import { ReportSeriesModel } from '../models/report-series.model';
import { JournodexRegionsService } from '../../Journodex/services/journodex-regions.service';
import { SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';
import { FacetResponseModel } from '../../../api/mentions/model/facet-response.model';
import { ChartMetricsEnum, SocialSeriesMetricsOnly } from '../enums/chart-metrics.enum';
import { FacetValueModel } from '../../../api/mentions/model/facet-value.model';
import { DateQueryModel } from '../../Forms/models/date-query.model';
import { MediaTypeModel } from '../../../Monitor/Shared/models/media-type.model';
import { SentimentModel } from '../../../Monitor/Shared/models/sentiment.model';
import { JournodexSourceModel } from '../../Journodex/models/journodex-source.model';
import { SearchCriteriaSourcesEnum } from '../../../api/mentions/enum/search-criteria-sources.enum';
import { ProminenceRecalculationJobStatus } from '../../../api/monitoring/monitoring-account-configuration.api';
import { userAppPreferencesDefault } from '../../../Core/Users/data/user-app-preferences-default';
import { UserAppPreferenceChartColors } from '../../../Core/Users/models/user-app-preferences-chart-colors.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { PreferencesModel } from '../../../Core/Users/models/preferences.model';
import { BucketResponseModel } from '../../../api/mentions/model/bucket-response.model';
import { ReportsNeoFacetKeys } from '../enums/reports-neo-facet-keys.enum';
import { NeoFacetResponseModel } from '../../../api/mentions/model/neo-facet-response.model';
import { safeDivide } from '../../../Core/utils';
import { CurrencyConversionService } from '../../../Core/Users/services/currency-conversion.service';
import { UserCurrency } from '../../../Core/Users/enums/user-currency';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { mediaTypeToPieMap } from './reports-series-mapper.service';
import { ReportsDataMappingKeysEnum } from '../models/reports-block-mapping-configuration.model';
import { JournodexGeographyModel } from '../../Journodex/models/journodex-geography.model';
import { GeographyTypesEnum } from '../../../api/journodex/enum/geography-types.enum';
import { monitoringFiltersEnum } from '../../../Monitor/Shared/enums/monitoring-filters.enum';
import { NeoFacetTermFieldApiEnum } from '../../../api/mentions/enum/neo-facet-term-field-api.enum';
import { JournodexGeographicEntityModel } from '../../Journodex/models/journodex-geographic-entity.model';
import { getTimeScale } from '../../../Common/Forms/helpers/time.helper';
import { MentionSearchQueryModel } from '../../../Monitor/Shared/models/mention-search-query.model';
import { DateFacetValueModel } from '../../../api/mentions/model/date-facet-value.model';
import { SocialSeriesMetricsEnum } from '../../../Social/Intelligence/enums/social-series-metrics.enum';
import { MediaTypeEnum } from '../../Basic/enums/media-type.enum';
import { AggregateDateFacetTypesEnum } from '../../../api/mentions/enum/aggregate-date-facet-types.enum';
import { chartMetricsToSentimentsMap, sentimentPercentMetrics } from '../maps/chart-metrics-to-sentiments.map';
import { viNeoFacetsToChartTypeSummaryTrendChartMap, viNeoFacetsOverTimeToCountMap } from '../../../VisibilityIntelligence/enums/vi-summary-chart-types.enum';


@Injectable()
export class ReportsGeneralDataMapperService implements OnDestroy {

    subscriptions: Subscription[] = [];
    prominenceStatus: ProminenceRecalculationJobStatus = ProminenceRecalculationJobStatus.DONE;

    userChartColors: UserAppPreferenceChartColors = userAppPreferencesDefault.reportsPrefs.defaultChartColors;
    preferences: PreferencesModel;
    timezone: string;
    hasTiktok: boolean = true;

    readonly NUMBER_ENTITIES = 10;
    readonly W_sov_visibility = 0.7;
    readonly W_rank_visibility = 0.3;
    readonly W_sov_presence = 0.6;
    readonly W_cite_presence = 0.4;

    /**
     * Class Constructor
     */
    constructor(
        public localizationService: LocalizationService,
        public journodexSearchService: JournodexRegionsService,
        public currentUserStateService: CurrentUserStateService,
        public currencyConversionService: CurrencyConversionService
    ) {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.preferences = prefs;
                this.timezone = prefs.timeZone;
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(x => x.unsubscribe());
    }

    /**
     * Responsible for determining and calling the appropriate map function
     */
    public mapFacetValues(response: MentionsSearchResponseModel, series: ReportSeriesModel): Observable<DataPointModel[]> {
        let dataPoints: Observable<DataPointModel[]>;
        dataPoints = this.mapDataByChartType(response, series);

        const metrics = series.metrics?.length > 0 ? series.metrics : [series.metric];
        if (metrics.includes(ChartMetricsEnum.ave)) {
            dataPoints = this.convertCurrency(dataPoints, series);
        }

        return dataPoints;
    }

    // Based on series.chartType to map mention search response to data points
    mapDataByChartType(response: MentionsSearchResponseModel, series: ReportSeriesModel): Observable<DataPointModel[]> {
        let dataPoints: Observable<DataPointModel[]>;

        switch(series.chartType) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                dataPoints = this.mapDateDataFromNeoFacetAsync(response, series);
                break;
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
                dataPoints = this.mapTop10DataFromNeoFacetAsync(response, series);
                break;
            case ReportBlockTypeEnum.TOP_HASHTAGS:
                dataPoints = this.mapTop10DataFromFacet(response, series);
                break;
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON:
                dataPoints = this.mapSentimentBreakdownDataFromFacet(response, series);
                break;
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN:
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON:
                dataPoints = this.mapMediaTypeBreakdownDataFromFacet(response, series);
                break;
            case ReportBlockTypeEnum.SHARE_OF_VOICE:
            case ReportBlockTypeEnum.COVERAGE_COMPARISON:
                dataPoints = this.mapShareOfVoiceDataFromFacet(response, series);
                break;
            case ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK:
                dataPoints = this.mapSocialCoverageByNetwork(response, series);
                break;
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                dataPoints = this.mapSentimentDateDataFromNeoFacetAsync(response, series);
                break;
            case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
                dataPoints = this.mapLocationDataFromNeoFacet(response, series);
                break;
            case ReportBlockTypeEnum.PROMINENCE_OVER_TIME:
                dataPoints = this.mapDateFacetValues(response, series);
                break;
            case ReportBlockTypeEnum.COVERAGE_METRIC:
                dataPoints = this.mapCoverageMetricDataFromFacet(response, series);

        }

        return dataPoints;
    }

    mapDateDataFromNeoFacetAsync(response: MentionsSearchResponseModel, series: ReportSeriesModel): Observable<DataPointModel[]> {
        const neoFacet = response?.neoFacetResults?.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
        if (!neoFacet) {
            return of([]);
        }

        return of(this.mapDateDataFromNeoFacet(response, series));
    }

    mapDateDataFromNeoFacet(response: MentionsSearchResponseModel, series: ReportSeriesModel): DataPointModel[] {
        const neoFacet = response?.neoFacetResults?.find(facet => facet.key === ReportsNeoFacetKeys.overtimeFacet);
        if (!neoFacet) {
            return [];
        }
        return neoFacet.buckets.map(
            (bucket, index) => {
                // amcharts only work with local timezone, so start and end time needs to change to user preference timezone (timestamp changed)
                let startTime = bucket.intervalValue.start;
                let endTime = bucket.intervalValue.end;
                if (this.timezone) {
                    startTime = moment(moment(startTime)
                        .tz(this.timezone).format('YYYY-MM-DD HH:mm:ss')).valueOf();
                    endTime = moment(moment(endTime)
                        .tz(this.timezone).format('YYYY-MM-DD HH:mm:ss')).valueOf();
                }

                const timeScale = getTimeScale(startTime, endTime);
                const momentStart = moment(startTime).startOf(timeScale);
                const momentEnd = moment(endTime).startOf(timeScale);
                const filterOverrides = new MentionSearchCriteriaModel();
                filterOverrides.query.dateQuery = DateQueryModel.getAbsoluteQuery();
                filterOverrides.query.dateQuery.dateType = series.dateRange?.dateType;
                // for generating the filters we should use the original start and end time
                filterOverrides.query.dateQuery.absoluteRange = {
                    start: new Date(bucket.intervalValue.start),
                    end: new Date(bucket.intervalValue.end)
                };
                const metrics = series.metrics?.length > 0 ? series.metrics : [ series.metric ];
                // Uncomment if we want to use user pref date format in the future
                // const momentFormat = dateFormats.find(x => x.value === this.preferences.dateFormat).momentFormat;
                const dataPoints: DataPointModel[] = [];
                metrics.forEach(metric => {
                    const valueLabel = series.chartType === ReportBlockTypeEnum.COVERAGE_OVER_TIME
                        ? this.localizationService.transform('Reports.formField.analysis.' + metric) : series.label;
                    const dataPoint = {
                        mappingKeys: {
                            [ReportsDataMappingKeysEnum.SOURCE]: series.label,
                            [ReportsDataMappingKeysEnum.DATE]: momentStart.valueOf(),
                            [ReportsDataMappingKeysEnum.METRIC]: metric,
                            [ReportsDataMappingKeysEnum.COVERAGE_IMPACT_TREND_METRIC]: metric,
                            [ReportsDataMappingKeysEnum.DATE_INDEX]: index,
                            [ReportsDataMappingKeysEnum.PERIOD]: series.hasPreviousPeriod ?
                                (series.mentions.isPreviousPeriod ? 0 : 1) : 0
                        },
                        value: this.getFacetFieldFromNeoFacet(bucket, metric),
                        valueLabel: valueLabel,
                        viewUrl: series.getMentionSeriesUrl(filterOverrides),
                        mentionCount: bucket.count,
                        startTimeExport: momentStart.format('YYYY/MM/DD HH:mm'),
                        endTimeExport: momentEnd.format('YYYY/MM/DD HH:mm'),
                        rawStartDate: bucket.intervalValue.start,
                        rawEndDate: bucket.intervalValue.end,
                        timeScale: timeScale
                    };
                    if (series.chartType === ReportBlockTypeEnum.COVERAGE_METRIC) {
                        dataPoint['bulletDisabled'] = index < neoFacet.buckets.length - 1;
                        if (metric === ChartMetricsEnum.ENGAGEMENTS_PER_POST || metric === ChartMetricsEnum.ENGAGEMENT_RATE) {
                            dataPoint['secondaryValue'] = this.getFacetFieldFromNeoFacet(bucket, ChartMetricsEnum.ENGAGEMENTS);
                        }
                    }
                    if (metric === ChartMetricsEnum.ave) {
                        dataPoint.value = +this.currencyConversionService.convertCurrency(dataPoint.value, false,
                            series?.styles?.currency ?? this.preferences.currency);
                    }
                    dataPoints.push(dataPoint);
                });

                return dataPoints;
            }
        ).flat();
    }

    mapDateViDataFromNeoFacet(
        neoFacet: NeoFacetResponseModel,
        series: ReportSeriesModel,
        key: ReportsNeoFacetKeys,
        doBooleanCount: boolean = false,
        overrideLabel?: string
    ): DataPointModel[] {
        if (!neoFacet) {
            return [];
        }
        const metrics = series.metrics?.length > 0 ? series.metrics : [series.metric];
        const label = overrideLabel ? overrideLabel : this.localizationService.transform(
            `Monitor.VISummarySeriesTypes.summary.${viNeoFacetsToChartTypeSummaryTrendChartMap.get(key)}.tooltipLabel`
        );

        let setValueNull = false;
        const useEnd = new Date().getTimezoneOffset() > 0;
        return neoFacet.buckets.map(
            (bucket, index) => {
                // we want to set the value to null so there is no data point, but that the line chart axis still shows that date range
                setValueNull = false;
                if (!doBooleanCount) {
                    const countKey = viNeoFacetsOverTimeToCountMap.get(key) || key.replace('_OVER_TIME', '_COUNT');
                    if (countKey &&
                        (!bucket?.buckets?.[countKey]?.[0]?.count ||
                        (bucket?.buckets?.[countKey]?.[0]?.count <= 0))
                    ) {
                        setValueNull = true;
                    }
                } else if (doBooleanCount && (bucket?.count <= 0)) {
                    setValueNull = true;
                }

                // amcharts only work with local timezone, so start and end time needs to change to user preference timezone (timestamp changed)
                let startTime = bucket.intervalValue.start;
                let endTime = bucket.intervalValue.end;
                // TODO: remove for now, drilldown does not convert user preferences
                // if (this.timezone) {
                //     startTime = moment(moment(startTime)
                //         .tz(this.timezone).format('YYYY-MM-DD HH:mm:ss')).valueOf();
                //     endTime = moment(moment(endTime)
                //         .tz(this.timezone).format('YYYY-MM-DD HH:mm:ss')).valueOf();
                // }

                let value = 0;
                if (setValueNull) {
                    value = null;
                } else if (doBooleanCount && (Array.isArray(bucket?.buckets?.[key])) ) {
                    let totalTrue = 0, totalFalse = 0;
                    for (const facet of bucket?.buckets?.[key]) {
                        if (facet.termValue === 'TRUE') {
                            totalTrue += facet.count;
                        } else if (facet.termValue === 'FALSE') {
                            totalFalse += facet.count;
                        }
                    };
                    value = ((totalTrue / (totalTrue+totalFalse)) * 100) || 0;
                } else {
                    value = (bucket?.buckets?.[key]?.[0]?.count || 0);
                }

                const timeScale = getTimeScale(startTime, endTime);
                const momentStart = moment(startTime).startOf(timeScale);
                const momentEnd = moment(endTime).startOf(timeScale);
                const dataPoints: DataPointModel[] = [];
                metrics.forEach(metric => {
                    const dataPoint = {
                        mappingKeys: {
                            [ReportsDataMappingKeysEnum.SOURCE]: label,
                            [ReportsDataMappingKeysEnum.DATE]: useEnd ? momentEnd.valueOf() : momentStart.valueOf(),
                            [ReportsDataMappingKeysEnum.METRIC]: overrideLabel ? overrideLabel : metric,
                            [ReportsDataMappingKeysEnum.DATE_INDEX]: index,
                            [ReportsDataMappingKeysEnum.PERIOD]: series.hasPreviousPeriod ?
                                (series.mentions.isPreviousPeriod ? 0 : 1) : 0
                        },
                        value: value,
                        valueLabel: label,
                        mentionCount: bucket.count,
                        startTimeExport: momentStart.format('YYYY/MM/DD HH:mm'),
                        endTimeExport: momentEnd.format('YYYY/MM/DD HH:mm'),
                        rawStartDate: bucket.intervalValue.start,
                        rawEndDate: bucket.intervalValue.end,
                        timeScale: timeScale,
                        viewUrl: '',
                        key
                    };
                    dataPoint.viewUrl = `?drilldown=${btoa(encodeURIComponent(JSON.stringify(dataPoint)))}`;
                    dataPoints.push(dataPoint);
                });
                return dataPoints;
            }
        ).flat();
    }

    calculateStrengthScoreFromNeoFacet(
        neoFacet: NeoFacetResponseModel,
        neoFacetBoolean: NeoFacetResponseModel,
        rankCount: string,
        rankOverTime: string,
        sentimentCount: string,
        sentimentOverTime: string,
        mentionedOverTime: string,
        domainCitedOverTime: string,
        label: string
    ): DataPointModel[] {
        const useEnd = new Date().getTimezoneOffset() > 0;
        return neoFacet?.buckets?.map(
            (bucket, index) => {
                let setValueNull = false;
                if (!bucket?.buckets?.[rankCount]?.[0]?.count || (bucket?.buckets?.[rankCount]?.[0]?.count <= 0)) {
                    setValueNull = true;
                }
                if (!bucket?.buckets?.[sentimentCount]?.[0]?.count || (bucket?.buckets?.[sentimentCount]?.[0]?.count <= 0)) {
                    setValueNull = true;
                }
                if (!neoFacetBoolean?.buckets?.[index]?.buckets?.[mentionedOverTime]?.[0]?.count ||
                    (neoFacetBoolean?.buckets?.[index]?.buckets?.[mentionedOverTime]?.[0]?.count <= 0)
                ) {
                    setValueNull = true;
                }
                if (!neoFacetBoolean?.buckets?.[index]?.buckets?.[domainCitedOverTime]?.[0]?.count ||
                    (neoFacetBoolean?.buckets?.[index]?.buckets?.[domainCitedOverTime]?.[0]?.count <= 0)
                ) {
                    setValueNull = true;
                }

                let value = null;
                let total = null;
                if (!setValueNull) {
                    const share_of_voice = this.calculateRateFromBooleanCounts(neoFacetBoolean?.buckets?.[index], mentionedOverTime);
                    const own_domain_cited_rate  = this.calculateRateFromBooleanCounts(neoFacetBoolean?.buckets?.[index], domainCitedOverTime);
                    // can only be a value between 0-100
                    const rank_score = Math.max(0,
                        Math.min((1 - ((bucket?.buckets?.[rankOverTime]?.[0]?.count || 0) - 1) / (this.NUMBER_ENTITIES)) * 100, 100)
                    );
                    const visibility_score = Math.round((share_of_voice * this.W_sov_visibility) + (rank_score * this.W_rank_visibility));
                    const presence_score = Math.round((share_of_voice * this.W_sov_presence) + (own_domain_cited_rate * this.W_cite_presence));
                    const sentiment_index = (bucket?.buckets?.[sentimentOverTime]?.[0]?.count || 0);

                    // value = strength score
                    value = (visibility_score + presence_score + sentiment_index) / 3;

                    total = this.calculateTotalFromBooleanCounts(neoFacetBoolean?.buckets?.[index], mentionedOverTime) +
                        this.calculateTotalFromBooleanCounts(neoFacetBoolean?.buckets?.[index], domainCitedOverTime) +
                        bucket?.buckets?.[rankCount]?.[0]?.count +
                        bucket?.buckets?.[sentimentCount]?.[0]?.count;
                }

                // amcharts only work with local timezone, so start and end time needs to change to user preference timezone (timestamp changed)
                let startTime = bucket.intervalValue.start;
                let endTime = bucket.intervalValue.end;
                // TODO: remove for now, drilldown does not convert user preferences
                // if (this.timezone) {
                //     startTime = moment(moment(startTime)
                //         .tz(this.timezone).format('YYYY-MM-DD HH:mm:ss')).valueOf();
                //     endTime = moment(moment(endTime)
                //         .tz(this.timezone).format('YYYY-MM-DD HH:mm:ss')).valueOf();
                // }

                const timeScale = getTimeScale(startTime, endTime);
                const momentStart = moment(startTime).startOf(timeScale);
                const momentEnd = moment(endTime).startOf(timeScale);
                const dataPoints: DataPointModel[] = [];
                const dataPoint = {
                    mappingKeys: {
                        [ReportsDataMappingKeysEnum.SOURCE]: label,
                        [ReportsDataMappingKeysEnum.DATE]: useEnd ? momentEnd.valueOf() : momentStart.valueOf(),
                        [ReportsDataMappingKeysEnum.METRIC]: label,
                        [ReportsDataMappingKeysEnum.DATE_INDEX]: index,
                    },
                    value: value,
                    valueLabel: label,
                    total: total,
                    mentionCount: bucket.count,
                    startTimeExport: momentStart.format('YYYY/MM/DD HH:mm'),
                    endTimeExport: momentEnd.format('YYYY/MM/DD HH:mm'),
                    rawStartDate: bucket.intervalValue.start,
                    rawEndDate: bucket.intervalValue.end,
                    timeScale: timeScale,
                    viewUrl: '',
                    key: ReportsNeoFacetKeys.viCompetitiveStrength
                };
                dataPoint.viewUrl = `?drilldown=${btoa(encodeURIComponent(JSON.stringify(dataPoint)))}`;
                dataPoints.push(dataPoint);

                return dataPoints;
            }
        ).flat() || [];
    }


    calculateRateFromBooleanCounts(bucket: BucketResponseModel, key: string): number {
        let totalTrue = 0, totalFalse = 0;
        if (Array.isArray(bucket?.buckets?.[key])) {
            for (const facet of bucket?.buckets?.[key]) {
                if (facet.termValue === 'TRUE') {
                    totalTrue += facet.count;
                } else if (facet.termValue === 'FALSE') {
                    totalFalse += facet.count;
                }
            };
        }
        return ((totalTrue / (totalTrue+totalFalse)) * 100) || 0;
    }

    calculateTotalFromBooleanCounts(bucket: BucketResponseModel, key: string): number {
        let totalTrue = 0, totalFalse = 0;
        if (Array.isArray(bucket?.buckets?.[key])) {
            for (const facet of bucket?.buckets?.[key]) {
                if (facet.termValue === 'TRUE') {
                    totalTrue += facet.count;
                } else if (facet.termValue === 'FALSE') {
                    totalFalse += facet.count;
                }
            };
        }
        return totalTrue + totalFalse || 0;
    }

    private mapDateFacetValues(
        response: MentionsSearchResponseModel,
        series: ReportSeriesModel,
    ): Observable<DataPointModel[]> {
        const data: DataPointModel[] = [];
        if (response &&
            response.dateFacetResults &&
            response.dateFacetResults[0] &&
            response.dateFacetResults[0].values
        ) {
            for (const facet of response.dateFacetResults[0].values) {
                const dateFacet = {
                    aggregateFacets: facet.aggregateFacets,
                    count: facet.count,
                    from: facet.from,
                    to: facet.to,
                    prominenceAverages: facet.prominenceAverages
                } as DateFacetValueModel;
                if (this.preferences && this.preferences.timeZone) {
                    dateFacet.from = moment(moment(dateFacet.from).tz(this.preferences.timeZone).format('YYYY-MM-DD HH:mm:ss')).valueOf();
                    dateFacet.to = moment(moment(dateFacet.to).tz(this.preferences.timeZone).format('YYYY-MM-DD HH:mm:ss')).valueOf();
                }

                const scale = getTimeScale(dateFacet.from, dateFacet.to);
                const date = moment(dateFacet.from).startOf(scale).toDate();

                const filterOverrides = new MentionSearchCriteriaModel();
                filterOverrides.query.dateQuery = DateQueryModel.getAbsoluteQuery();
                filterOverrides.query.dateQuery.absoluteRange = {
                    start: new Date(facet.from),
                    end: new Date(facet.to)
                };

                // HACK : prevent data from going into the chart if we recalculation is under way
                // But in reality we should instead prevent the series service from fetching it at all
                // This can be achieved when the slow/fast facet system gets replaced.
                if (this.prominenceStatus !== ProminenceRecalculationJobStatus.DONE) {
                    continue;
                }

                const dataPoint = {
                    mappingKeys: {
                        [ReportsDataMappingKeysEnum.DATE]: date.getTime(),
                    },
                    label: scale === 'hour' ? date.toTimeString().substring(0, 5) : date.toDateString(),
                    startTimeExport: moment(dateFacet.from).format('YYYY/MM/DD HH:mm'),
                    endTimeExport: moment(dateFacet.to).format('YYYY/MM/DD HH:mm'),
                    rawStartDate: facet.from,
                    rawEndDate: facet.to,
                    value: this.getFacetFieldFromDateFacet(dateFacet, series, series.prominenceTrendGroup?.id),
                    // for prominence over time chart, yValue is average prominence instead of mentions and we want to display mention count
                    // thus we also pass dateFacet.count
                    mentionCount: dateFacet.count,
                    // for identifying source on chart segment click later
                    viewUrl: series.getMentionSeriesUrl(filterOverrides),
                    timeScale: scale
                };
                if (series.metric === ChartMetricsEnum.ave) {
                    dataPoint.value = +this.currencyConversionService.convertCurrency(dataPoint.value, false,
                        series?.styles?.currency ?? this.preferences.currency);
                }
                data.push(dataPoint);
            }

        }
        return of(data);
    }


    private getFacetFieldFromDateFacet(
        dateFacet: DateFacetValueModel,
        series: ReportSeriesModel,
        prominenceGroupId?: number,
    ): number {
        if (series.metric === ChartMetricsEnum.AVERAGE_DOMAIN_AUTHORITY) {
            return safeDivide(dateFacet?.aggregateFacets?.[AggregateDateFacetTypesEnum.SUM_DOMAIN_AUTHORITY], dateFacet.count);
        }
        if (dateFacet.prominenceAverages) {
            if (dateFacet.prominenceAverages[prominenceGroupId]) {
                // Normalize to 0-5 value to facilitate usage in the chart
                return Math.round(dateFacet.prominenceAverages[prominenceGroupId] * 5 * 100) / 100;
            }
        }
        return 0;
    }

    mapSentimentDateDataFromNeoFacetAsync(
        response: MentionsSearchResponseModel,
        series: ReportSeriesModel,
        sentiments: SearchCriteriaQuerySentimentsEnum[] = series.sentiments
    ): Observable<DataPointModel[]> {
        const neoFacet = response?.neoFacetResults?.find(
            facet => facet.key === ReportsNeoFacetKeys.sentimentOverTime);
        if (!neoFacet) {
            return of([]);
        }

        return of(this.mapSentimentDateDataFromNeoFacet(response, series, sentiments));
    }

    mapSentimentDateDataFromNeoFacet(
        response: MentionsSearchResponseModel,
        series: ReportSeriesModel,
        selectedSentiments: SearchCriteriaQuerySentimentsEnum[] = series.sentiments,
        allSentiments: SearchCriteriaQuerySentimentsEnum[] = series.sentiments
    ): DataPointModel[] {
        const neoFacet = response?.neoFacetResults?.find(
            facet => facet.key === ReportsNeoFacetKeys.sentimentOverTime);
        if (!neoFacet) {
            return [];
        }
        return neoFacet.buckets.map(
            bucket => {
                const positiveSum = (bucket.buckets[ReportsNeoFacetKeys.sentimentTrend] as BucketResponseModel[]).reduce(
                    (previousValue, bucketValue) => {
                        return +bucketValue.termValue >= 0.3 ? previousValue + bucketValue.count : previousValue;
                    }, 0
                );

                const neutralSum = (bucket.buckets[ReportsNeoFacetKeys.sentimentTrend] as BucketResponseModel[]).reduce(
                    (previousValue, bucketValue) => {
                        return +bucketValue.termValue < 0.3 && +bucketValue.termValue > -0.3 ? previousValue + bucketValue.count : previousValue;
                    }, 0
                );
                const negativeSum = (bucket.buckets[ReportsNeoFacetKeys.sentimentTrend] as BucketResponseModel[]).reduce(
                    (previousValue, bucketValue) => {
                        return +bucketValue.termValue <= -0.3 ? previousValue + bucketValue.count : previousValue;
                    }, 0
                );

                // amcharts only work with local timezone, so start and end time needs to change to user preference timezone (timestamp changed)
                let startTime = bucket.intervalValue.start;
                let endTime = bucket.intervalValue.end;
                if (this.timezone) {
                    startTime = moment(moment(startTime)
                        .tz(this.timezone).format('YYYY-MM-DD HH:mm:ss')).valueOf();
                    endTime = moment(moment(endTime)
                        .tz(this.timezone).format('YYYY-MM-DD HH:mm:ss')).valueOf();
                }

                const timeScale = getTimeScale(startTime, endTime);
                const momentStart = moment(startTime).startOf(timeScale);
                const momentEnd = moment(endTime).startOf(timeScale);
                const filterOverrides = new MentionSearchCriteriaModel();
                filterOverrides.query.dateQuery = DateQueryModel.getAbsoluteQuery();
                filterOverrides.query.dateQuery.dateType = series.dateRange?.dateType;
                // for generating the filters we should use the original start and end time
                filterOverrides.query.dateQuery.absoluteRange = {
                    start: new Date(bucket.intervalValue.start),
                    end: new Date(bucket.intervalValue.end)
                };
                // Uncomment if we want to use user pref date format in the future
                // const momentFormat = dateFormats.find(x => x.value === this.preferences.dateFormat).momentFormat;
                const dataPoints: DataPointModel[] = [];
                const count = this.countTotalMentions(
                    allSentiments, positiveSum, neutralSum, negativeSum, bucket.count - positiveSum - neutralSum - negativeSum
                );
                selectedSentiments.forEach(sentiment => {
                    const valueLabel = this.localizationService.transform(`Sentiment.${sentiment}`);
                    let val: number = 0;
                    switch (sentiment) {
                        case SearchCriteriaQuerySentimentsEnum.positive:
                            val = positiveSum;
                            break;
                        case SearchCriteriaQuerySentimentsEnum.neutral:
                            val = neutralSum;
                            break;
                        case SearchCriteriaQuerySentimentsEnum.negative:
                            val = negativeSum;
                            break;
                        default:
                            val = bucket.count - positiveSum - negativeSum - neutralSum;
                    }
                    filterOverrides.query.sentiments =[new SentimentModel(sentiment)];
                    const dataPoint = {
                        mappingKeys: {
                            [ReportsDataMappingKeysEnum.DATE]: momentStart.valueOf(),
                            [ReportsDataMappingKeysEnum.SENTIMENT]: sentiment,
                        },
                        value: val,
                        percent: parseFloat((safeDivide(val, bucket.count) * 100).toFixed(1)),
                        valueLabel: valueLabel,
                        viewUrl: series.getMentionSeriesUrl(filterOverrides),
                        mentionCount: count,
                        startTimeExport: momentStart.format('YYYY/MM/DD HH:mm'),
                        endTimeExport: momentEnd.format('YYYY/MM/DD HH:mm'),
                        rawStartDate: bucket.intervalValue.start,
                        rawEndDate: bucket.intervalValue.end,
                        timeScale: timeScale
                    };
                    dataPoints.push(dataPoint);
                });

                return dataPoints;
            }
        ).flat();
    }

    countTotalMentions(sentiments, positiveSum, neutralSum, negativeSum, notAvailableSum): number {
        let count = 0;
        sentiments.forEach(sentiment => {
            if (SearchCriteriaQuerySentimentsEnum.positive === sentiment) {
                count += positiveSum;
            } else if (SearchCriteriaQuerySentimentsEnum.neutral === sentiment) {
                count += neutralSum;
            } else if (SearchCriteriaQuerySentimentsEnum.negative === sentiment) {
                count += negativeSum;
            } else {
                count += notAvailableSum;
            }
        });
        return count;
    }

    mapTop10DataFromNeoFacetAsync(response: MentionsSearchResponseModel, series: ReportSeriesModel): Observable<DataPointModel[]> {
        return of(this.mapTop10DataFromNeoFacet(response, series));
    }

    mapTop10DataFromNeoFacet(response: MentionsSearchResponseModel, series: ReportSeriesModel): DataPointModel[] {
        const isEntityOutlet = series.chartType === ReportBlockTypeEnum.TOP_10_OUTLETS;
        const facetResults =  response.neoFacetResults.find(facet => {
            return isEntityOutlet ?
                facet.key === NeoFacetTermFieldApiEnum.OUTLET_NAME_FACET
                : facet.key === NeoFacetTermFieldApiEnum.CONTACT_NAME;
        })?.buckets;
        const data: DataPointModel[] = [];
        if (facetResults) {
            let totalResults = 0;
            for (const facetValue of facetResults) {
                let facetCount;
                switch (series.metric) {
                    case ChartMetricsEnum.volumeOfMentions:
                    case ChartMetricsEnum.VOLUME:
                        facetCount = facetValue.count;
                        break;
                    case ChartMetricsEnum.IMPRESSIONS:
                        facetCount = facetValue.buckets[NeoFacetTermFieldApiEnum.AUDIENCE_REACH][0].count;
                        break;
                    case ChartMetricsEnum.ave:
                        facetCount = facetValue.buckets[NeoFacetTermFieldApiEnum.AD_VALUE_USD][0].count;
                        break;
                    case ChartMetricsEnum.domainAuthority:
                        facetCount = facetValue.buckets[NeoFacetTermFieldApiEnum.DOMAIN_AUTHORITY][0].count;
                        break;
                }
                if (!facetCount || facetValue.termValue === '') {
                    continue;
                }
                const dataPoint: DataPointModel = {
                    label: facetValue.termValue.replace(/[\r\n]+/g, ' '),
                    uuid: isEntityOutlet ?
                        facetValue.buckets[NeoFacetTermFieldApiEnum.OUTLET_NAME_X_OUTLET_UUID]?.[0]?.termValue
                        : facetValue.buckets[NeoFacetTermFieldApiEnum.CONTACT_NAME_X_CONTACT_UUID]?.[0]?.termValue,
                    mappingKeys: {},
                    value: facetCount
                };
                totalResults += facetCount;
                const s = series.clone();
                const entity = new JournodexSourceModel();
                entity.entityType = isEntityOutlet ? SearchCriteriaSourcesEnum.outlet : SearchCriteriaSourcesEnum.journalist;
                entity.entityName = facetValue.termValue;
                if (s.mentions.combinedQuery) {
                    s.mentions.combinedQuery.includedSources = [ entity ];
                }
                dataPoint.mappingKeys[isEntityOutlet ? 'outlet' : 'author'] = facetValue.termValue;
                dataPoint['viewUrl'] = s.getMentionSeriesUrl();

                data.push(dataPoint);
            }
            data.forEach(x => {
                x.percent = parseFloat((x.value / totalResults * 100).toFixed(1));
            });
        }

        return data.sort((a, b) => b.value - a.value);
    }

    mapTop10DataFromFacet(response: MentionsSearchResponseModel, series: ReportSeriesModel): Observable<DataPointModel[]> {
        const metric: ChartMetricsEnum = series.metric;
        const facetField: MentionSearchFacetTypesEnum = this.findTop10FacetField(series, metric);
        const data: DataPointModel[] = [];

        if (response && response.facetResults) {
            const facetResults: FacetResponseModel = response.facetResults.find(facetType => {
                return facetType.field === facetField;
            });

            if (!facetResults) {
                console.error('no facet results for facet: ', facetField);
                return of(data);
            }

            // ave uses the 'sum' field instead of the count field
            const totalResults = facetResults.values.reduce((x, y) => x + (this.isValueSum(facetField) ? y.sum : y.count), 0);
            for (const facetValue of facetResults.values) {
                if (!facetValue.value || facetValue.value.trim() === '') {
                    // Ignore facets with empty labels
                    continue;
                }

                const count = this.mapXValueByFacet(facetValue, facetField);
                const percent = parseFloat(((this.isValueSum(facetField) ? facetValue.sum : facetValue.count) / totalResults * 100).toFixed(1));
                const dataPoint: DataPointModel = {
                    label: facetValue.value.replace(/[\r\n]+/g, ' '),
                    mappingKeys: {},
                    value: count,
                    percent: percent
                };

                const s = series.clone();
                switch (facetField) {
                    case MentionSearchFacetTypesEnum.contact:
                    case MentionSearchFacetTypesEnum.authorByAVE:
                    case MentionSearchFacetTypesEnum.authorByAudienceReach:
                        const author = new JournodexSourceModel();
                        author.entityType = SearchCriteriaSourcesEnum.journalist;
                        author.entityName = facetValue.value;
                        if (!s.mentions.combinedQuery) {
                            s.mentions.combinedQuery = new MentionSearchQueryModel();
                        }
                        s.mentions.combinedQuery.includedSources = [ author ];

                        dataPoint.mappingKeys['author'] = facetValue.value;
                        break;
                    case MentionSearchFacetTypesEnum.outlet:
                    case MentionSearchFacetTypesEnum.outletByAVE:
                    case MentionSearchFacetTypesEnum.outletByAudienceReach:
                    case MentionSearchFacetTypesEnum.outletByDomainAuthority:
                        const outlet = new JournodexSourceModel();
                        outlet.entityType = SearchCriteriaSourcesEnum.outlet;
                        outlet.entityName = facetValue.value;
                        if (!s.mentions.combinedQuery) {
                            s.mentions.combinedQuery = new MentionSearchQueryModel();
                        }
                        s.mentions.combinedQuery.includedSources = [ outlet ];

                        dataPoint.mappingKeys['outlet'] = facetValue.value;
                        break;
                    case MentionSearchFacetTypesEnum.hashtags:
                        if (!s.mentions.combinedQuery) {
                            s.mentions.combinedQuery = new MentionSearchQueryModel();
                        }
                        s.mentions.combinedQuery.hashtags.include = [ facetValue.value ];
                        dataPoint.mappingKeys['hashtag'] = facetValue.value;
                        break;
                }

                dataPoint['viewUrl'] = s.getMentionSeriesUrl();

                if (count) {
                    data.push(dataPoint);
                }
            }
        }

        return of(data);
    }

    mapSentimentBreakdownDataFromFacet(response: MentionsSearchResponseModel, series: ReportSeriesModel): Observable<DataPointModel[]> {
        const data = [];
        const sentimentValues = this.getSentimentValuesFromResponse(response.facetResults);

        const totalResults = series.sentiments.reduce((sum, sentiment) => {
            return sum + (sentimentValues[sentiment] || 0);
        }, 0);

        for (const key of series.sentiments) {

            const value = sentimentValues[key];
            if (!series.mentions.combinedQuery) {
                series.mentions.combinedQuery = new MentionSearchQueryModel();
            }
            series.mentions.combinedQuery.sentiments = [new SentimentModel(key as SearchCriteriaQuerySentimentsEnum)];

            data.push({
                label: series.label,
                mappingKeys: {
                    source: series.label,
                    sentiment: key
                },
                value: value,
                valueLabel: this.localizationService.transform(`Sentiment.${key}`),
                percent: parseFloat((safeDivide(value, totalResults) * 100).toFixed(1)),
                mentionCount: value,
                viewUrl: series.getMentionSeriesUrl(),
            });
        }

        series.mentions.combinedQuery.sentiments = [];

        return of(data);
    }

    mapMediaTypeBreakdownDataFromFacet(response: MentionsSearchResponseModel, series: ReportSeriesModel): Observable<DataPointModel[]> {
        const data: DataPointModel[] = [];

        if (response && response.facetResults) {
            for (const facetType of response.facetResults) {
                if (facetType.field !== MentionSearchFacetTypesEnum.mediaType) {
                    continue;
                }

                const mediaTypeBreakdownDataMap: Map<MediaTypeEnum, DataPointModel> = new Map();
                for (const facetValue of facetType.values) {
                    if (!facetValue.count) {
                        continue;
                    }
                    let dataPoint: DataPointModel;
                    const mediaType = facetValue.value as MediaTypeEnum;
                    if (!!series.styles.networkSelection && !series.styles.networkSelection.includes(mediaType)) {
                        continue;
                    }
                    if (mediaType === MediaTypeEnum.TIKTOK && !this.hasTiktok) {
                        continue;
                    }

                    const mapping = mediaTypeToPieMap.get(mediaType);
                    if (mapping) {
                        const s = series.clone();
                        if (!s.mentions.combinedQuery) {
                            s.mentions.combinedQuery = new MentionSearchQueryModel();
                        }
                        s.mentions.combinedQuery.includedMediaTypes = [ new MediaTypeModel(mediaType) ];

                        dataPoint = {
                            label: series.label,
                            mappingKeys: {
                                source: series.label,
                                mediaType: mediaType
                            },
                            value: facetValue.count,
                            valueLabel: this.localizationService.transform(mapping.label),
                            percent: parseFloat((facetValue.count / response.totalResults * 100).toFixed(1)),
                            mentionCount: facetValue.count,
                            viewUrl: s.getMentionSeriesUrl()
                        };

                        mediaTypeBreakdownDataMap.set(mediaType, dataPoint);
                    }
                }

                // This is so that they always appear in the order specified by the mediaTypeToPieMap
                for (const [key, value] of Array.from(mediaTypeToPieMap)) {
                    if (mediaTypeBreakdownDataMap.get(key)) {
                        data.push(mediaTypeBreakdownDataMap.get(key));
                    }
                }
            }
        }

        return of(data);
    }

    mapShareOfVoiceDataFromFacet(response: MentionsSearchResponseModel, series: ReportSeriesModel): Observable<DataPointModel[]> {
        const data: DataPointModel[] = [];
        let totalResults = response?.totalResults ?? 0;
        let startDate;

        if (response) {
            if (series.metric) {
                if ((series.metric === ChartMetricsEnum.ave) ||
                    (series.metric === ChartMetricsEnum.audienceReach) || (series.metric === ChartMetricsEnum.IMPRESSIONS)
                ) {
                    if (response.dateFacetResults && response.dateFacetResults[0]) {
                        if (series.metric === ChartMetricsEnum.ave) {
                            totalResults = 0;
                            for (const dateFacetResult of response.dateFacetResults[0].values) {
                                totalResults += (dateFacetResult.aggregateFacets?.SUM_AD_VALUE_USD || 0);
                            }
                        } else if ((series.metric === ChartMetricsEnum.audienceReach) || (series.metric === ChartMetricsEnum.IMPRESSIONS)) {
                            totalResults = 0;
                            for (const dateFacetResult of response.dateFacetResults[0].values) {
                                totalResults += (dateFacetResult.aggregateFacets?.SUM_AUDIENCE_REACH || 0);
                            }
                        }
                    }
                } else if (SocialSeriesMetricsOnly.includes(series.metric as SocialSeriesMetricsEnum)) {
                    totalResults = 0;
                    const neoFacetResults: NeoFacetResponseModel = response.neoFacetResults?.find(
                        neofacetType => neofacetType.key === ReportsNeoFacetKeys.overtimeFacet
                    );
                    if (neoFacetResults) {
                        for (const neoFacetValue of neoFacetResults.buckets) {
                            totalResults += this.getMetricValue(neoFacetValue, series.metric);
                        }
                    }
                }
            }

            if (series.mentions.isPreviousPeriod && series.dateRange.type === DateQueryTypeEnum.ALL) {
                totalResults = 0;
            }
        }

        data.push({
            mappingKeys: {
                source: series.label,
                date: series.mentions.isPreviousPeriod ? 0 : 1
            },
            value: totalResults,
            // percent
            // todo: remove previous period
            viewUrl: series.getMentionSeriesUrl(null, series.mentions.isPreviousPeriod),
        });

        return of(data);
    }

    mapSocialCoverageByNetwork(
        response: MentionsSearchResponseModel,
        series: ReportSeriesModel
    ): Observable<DataPointModel[]> {
        const data: DataPointModel[] = [];
        const metric = series?.metric;
        const networkSelection = series?.styles?.networkSelection;

        if (networkSelection?.length) {
            if (response && response.neoFacetResults) {
                const neoFacetField = ReportsNeoFacetKeys.socialCoverageByNetwork;
                const neoFacetResults: NeoFacetResponseModel = response.neoFacetResults?.find(neofacetType => {
                    return neofacetType.key === neoFacetField;
                });

                if (!neoFacetResults) {
                    return of(data);
                }

                let totalCount = 0;
                for (const neoFacetValue of neoFacetResults.buckets) {
                    if (!neoFacetValue.termValue || neoFacetValue.termValue.trim() === '') {
                        // Ignore facets with empty labels
                        continue;
                    }
                    if (
                        (neoFacetValue.termValue === 'TWEET' && includes(networkSelection, MediaTypeEnum.TWITTER)) ||
                        includes(networkSelection, neoFacetValue.termValue)
                    ) {
                        const value = this.getMetricValue(neoFacetValue, metric);
                        let mediaType = neoFacetValue.termValue;
                        // map 'TWEET' for backwards compatibility
                        if (neoFacetValue.termValue === 'TWEET') {
                            mediaType = MediaTypeEnum.TWITTER;
                        }
                        totalCount += neoFacetValue.count;
                        const s = series.clone();

                        if (!s.mentions.combinedQuery) {
                            s.mentions.combinedQuery = new MentionSearchQueryModel();
                        }

                        s.mentions.combinedQuery.includedMediaTypes = [new MediaTypeModel(mediaType as MediaTypeEnum)];

                        data.push({
                            mappingKeys: {
                                mediaType: mediaType,
                                date: series.mentions.isPreviousPeriod ? 0 : 1
                            },
                            value: value,
                            valueLabel: this.localizationService.transform(`MediaType.${mediaType}`),
                            mentionCount: neoFacetValue.count,
                            // todo: remove previous period
                            viewUrl: s.getMentionSeriesUrl(null, series.mentions.isPreviousPeriod),
                        });
                    }
                }
                data.forEach(x => {
                    x.percent = parseFloat((safeDivide(x.mentionCount as number, totalCount) * 100).toFixed(1));
                });
            }
        }

        return of(data);
    }

    mapLocationDataFromNeoFacet(response: MentionsSearchResponseModel, series: ReportSeriesModel): Observable<DataPointModel[]> {
        if (series.styles.geographyScale === GeographyTypesEnum.country) {
            return this.mapCountryDataFromNeoFacet(response, series);
        } else {
            return this.mapWorldDataFromNeoFacet(response, series);
        }
    }

    mapCountryDataFromNeoFacet(response: MentionsSearchResponseModel, series: ReportSeriesModel): Observable<DataPointModel[]> {
        const dataPointsResponse: BehaviorSubject<DataPointModel[]> =  new BehaviorSubject<DataPointModel[]>(null);
        const countryJournodexId = +this.journodexSearchService.getCountryByIsoCode(series.styles.regionIsoCode + '').id;
        const request = this.getCountrySubdivisions(countryJournodexId, <GeographyTypesEnum>series.styles.geographyScale);

        request.subscribe(provinces => {
            const data = [];
            if (response && response.facetResults) {
                let total = 0;
                let currentTotal = 0;
                let value = 0;

                for (const facetType of response.neoFacetResults) {
                    if (facetType.key !== MentionSearchFacetTypesEnum.province) {
                        continue;
                    }

                    for (const facetValue of facetType.buckets) {
                        switch (series.metric) {
                            case ChartMetricsEnum.volumeOfMentions:
                                value = facetValue.count;
                                break;
                            case ChartMetricsEnum.IMPRESSIONS:
                                value = facetValue.buckets[NeoFacetTermFieldApiEnum.AUDIENCE_REACH][0]?.count || 0;
                                total += value;
                                break;
                            case ChartMetricsEnum.ave:
                                value = facetValue.buckets[NeoFacetTermFieldApiEnum.AD_VALUE_USD][0]?.count || 0;
                                total += value;

                        }
                        const region = provinces.find(province => (province.province.id === +facetValue.termValue));
                        if (!region || value === 0) {
                            continue;
                        }
                        currentTotal += value;
                        let viewUrl;
                        let filterErrorMessage;
                        // HACK some region filters cannot be safely applied to coverage by region chart
                        try {
                            const s = series.clone();
                            if (!s.mentions.combinedQuery) {
                                s.mentions.combinedQuery = new MentionSearchQueryModel();
                            }
                            s.mentions.combinedQuery.includedRegions = [ region ];
                            viewUrl = s.getMentionSeriesUrl();
                        } catch (error) {
                            filterErrorMessage = this.generateFilterErrorMessage(error);
                        }

                        data.push({
                            id: `${series.styles.regionIsoCode}-${region.isoCode}`, // this property seems to be required by amchart internally
                            label: region.province.name,
                            mappingKeys: {
                                [ReportsDataMappingKeysEnum.LOCATION]: `${series.styles.regionIsoCode}-${region.isoCode}`
                            },
                            value: value,
                            mentionCount: facetValue.count,
                            viewUrl: viewUrl,
                            filterErrorMessage: filterErrorMessage
                        });
                    }
                    data.sort((a, b) => b.value - a.value);

                    // Country based chart, for mentions that do not have province info, we set its id to null
                    // this will be added to 'other' legend in map chart
                    total = series.metric === ChartMetricsEnum.volumeOfMentions ? response.totalResults : total;
                    if (currentTotal < total) {
                        data.push({
                            id: null, // this property seems to be required by amchart internally
                            // label: 'region.province.name',
                            label: this.localizationService.transform('Common.other'),
                            mappingKeys: {
                                [ReportsDataMappingKeysEnum.LOCATION]: 'other'
                            },
                            value: total - currentTotal
                        });
                    }

                    data.forEach(dataPoint => {
                        dataPoint.percent = parseFloat((dataPoint.value / total * 100).toFixed(1));
                    });
                }
            }

            dataPointsResponse.next(data);
        });

        return dataPointsResponse;
    }

    mapWorldDataFromNeoFacet(response: MentionsSearchResponseModel, series: ReportSeriesModel): Observable<DataPointModel[]> {
        const data = [];
        const neoFacet = response.neoFacetResults.find(facet => facet.key === NeoFacetTermFieldApiEnum.COUNTRY);
        if (!neoFacet) {
            return of(data);
        }
        let total = 0;
        let value = 0;

        neoFacet.buckets.forEach(bucket => {
            switch (series.metric) {
                case ChartMetricsEnum.volumeOfMentions:
                case ChartMetricsEnum.VOLUME:
                    value = bucket.count;
                    break;
                case ChartMetricsEnum.IMPRESSIONS:
                    value = bucket.buckets[NeoFacetTermFieldApiEnum.AUDIENCE_REACH][0]?.count || 0;
                    total += value;
                    break;
                case ChartMetricsEnum.ave:
                    value = bucket.buckets[NeoFacetTermFieldApiEnum.AD_VALUE_USD][0]?.count || 0;
                    total += value;
            }
            const country = this.journodexSearchService.getCountryById(bucket.termValue);
            if (!country || value === 0) {
                return;
            }
            const geoModel = new JournodexGeographyModel();
            geoModel.geographicType = GeographyTypesEnum.country;
            geoModel.id = +country.id;

            let viewUrl;
            let filterErrorMessage;
            // HACK some region filters cannot be safely applied to coverage by region chart
            try {
                const s = series.clone();
                if (!s.mentions.combinedQuery) {
                    s.mentions.combinedQuery = new MentionSearchQueryModel();
                }
                s.mentions.combinedQuery.includedRegions = [ geoModel ];
                viewUrl = s.getMentionSeriesUrl();
            } catch (error) {
                filterErrorMessage = this.generateFilterErrorMessage(error);
            }

            data.push({
                id: country.countryCode, // this property seems to be required by amchart internally
                label: country.name,
                mappingKeys: {
                    [ReportsDataMappingKeysEnum.LOCATION]: country.countryCode
                },
                value: value,
                mentionCount: bucket.count,
                viewUrl: viewUrl,
                filterErrorMessage: filterErrorMessage
            });
        });
        data.sort((a, b) => b.value - a.value);
        total = series.metric === ChartMetricsEnum.volumeOfMentions ? response.totalResults : total;
        data.forEach((dataPoint) => {
            dataPoint.percent = parseFloat((dataPoint.value / total * 100).toFixed(1));
        });

        return of(data);
    }

    mapCoverageMetricDataFromFacet(response: MentionsSearchResponseModel, series: ReportSeriesModel): Observable<DataPointModel[]> {
        let data;
        switch (series.metric) {
            case ChartMetricsEnum.volumeOfMentions:
            case ChartMetricsEnum.IMPRESSIONS:
            case ChartMetricsEnum.ave:
            case ChartMetricsEnum.ENGAGEMENTS:
            case ChartMetricsEnum.ENGAGEMENTS_PER_POST:
            case ChartMetricsEnum.ENGAGEMENT_RATE:
            case ChartMetricsEnum.APPLAUSE:
            case ChartMetricsEnum.CONVERSATION:
            case ChartMetricsEnum.AMPLIFICATION:
            case ChartMetricsEnum.VIEWS:
                return this.mapDateDataFromNeoFacetAsync(response, series);
            case ChartMetricsEnum.positiveSentimentPercentage:
            case ChartMetricsEnum.neutralSentimentPercentage:
            case ChartMetricsEnum.negativeSentimentPercentage:
            case ChartMetricsEnum.POSITIVE:
            case ChartMetricsEnum.NEUTRAL:
            case ChartMetricsEnum.NEGATIVE:
                data = this.mapSentimentDateDataFromNeoFacet(
                    response,
                    series,
                    [chartMetricsToSentimentsMap.get(series.metric)],
                    Object.values(SearchCriteriaQuerySentimentsEnum)
                );
                data.forEach((x, i) => {
                    if (sentimentPercentMetrics.includes(series.metric)) {
                        const mentionCount = x.value;
                        x.value = x.percent;
                        x['secondaryValue'] = mentionCount;
                    }
                    x.mappingKeys[ReportsDataMappingKeysEnum.DATE_INDEX] = i;
                    x.mappingKeys[ReportsDataMappingKeysEnum.PERIOD] = series.hasPreviousPeriod ?
                        (series.mentions.isPreviousPeriod ? 0: 1) : 0;
                    x['bulletDisabled'] = i < data.length - 1;
                });
                return of(data);
            case ChartMetricsEnum.AVERAGE_DOMAIN_AUTHORITY:
                return of(this.getAverageDomainAuthorityFromDateFacet(response, series));
            case ChartMetricsEnum.custom:
                return of([]);
        }
    }

    getAverageDomainAuthorityFromDateFacet(response: MentionsSearchResponseModel, series: ReportSeriesModel): DataPointModel[] {
        const data = [];
        const results = response.dateFacetResults?.[0]?.values;
        if (results) {
            results.forEach((facet, index) => {
                const dateFacet = {
                    aggregateFacets: facet.aggregateFacets,
                    count: facet.count,
                    from: facet.from,
                    to: facet.to,
                } as DateFacetValueModel;
                if (this.preferences?.timeZone) {
                    dateFacet.from = moment(moment(dateFacet.from).tz(this.preferences.timeZone).format('YYYY-MM-DD HH:mm:ss')).valueOf();
                    dateFacet.to = moment(moment(dateFacet.to).tz(this.preferences.timeZone).format('YYYY-MM-DD HH:mm:ss')).valueOf();
                }

                const scale = getTimeScale(dateFacet.from, dateFacet.to);
                const date = moment(dateFacet.from).startOf(scale).toDate();

                const filterOverrides = new MentionSearchCriteriaModel();
                filterOverrides.query.dateQuery = DateQueryModel.getAbsoluteQuery();
                filterOverrides.query.dateQuery.absoluteRange = {
                    start: new Date(facet.from),
                    end: new Date(facet.to)
                };

                const dataPoint = {
                    mappingKeys: {
                        [ReportsDataMappingKeysEnum.DATE]: date.getTime(),
                        [ReportsDataMappingKeysEnum.DATE_INDEX]: index,
                        [ReportsDataMappingKeysEnum.PERIOD]: series.hasPreviousPeriod ?
                            (series.mentions.isPreviousPeriod ? 0 : 1) : 0
                    },
                    label: scale === 'hour' ? date.toTimeString().substring(0, 5) : date.toDateString(),
                    startTimeExport: moment(dateFacet.from).format('YYYY/MM/DD HH:mm'),
                    endTimeExport: moment(dateFacet.to).format('YYYY/MM/DD HH:mm'),
                    rawStartDate: facet.from,
                    rawEndDate: facet.to,
                    value: this.getFacetFieldFromDateFacet(dateFacet, series),
                    mentionCount: dateFacet.count,
                    viewUrl: series.getMentionSeriesUrl(filterOverrides),
                    timeScale: scale
                };
                if (series.chartType === ReportBlockTypeEnum.COVERAGE_METRIC) {
                    dataPoint['bulletDisabled'] = index < results.length - 1;
                    dataPoint['secondaryValue'] = dateFacet?.aggregateFacets?.[AggregateDateFacetTypesEnum.SUM_DOMAIN_AUTHORITY];
                }

                data.push(dataPoint);
            });
        }

        return data;
    }

    // Convert AVE value to user pref currency
    convertCurrency(data: Observable<DataPointModel[]>, series: ReportSeriesModel): Observable<DataPointModel[]> {
        const chartsWithOwnCurrencyLogic = [
            ReportBlockTypeEnum.COVERAGE_SUMMARY,
            ReportBlockTypeEnum.TOP_TERMS,
            ReportBlockTypeEnum.COVERAGE_METRIC,
            ReportBlockTypeEnum.COVERAGE_OVER_TIME
        ];
        if (chartsWithOwnCurrencyLogic.includes(series.chartType)) {
            return data;
        }

        const reportCurrency: UserCurrency = series.styles.currency || this.preferences.currency;

        return data.pipe(
            tap(points => {
                points.forEach(x => x.value = +this.currencyConversionService.convertCurrency(x.value, false, reportCurrency));
            })
        );
    }

    setTimezone(timezone: string): void {
        this.timezone = timezone;
    }

    //#region private helper methods for individual mapping methods

    private getFacetFieldFromNeoFacet(
        bucket: BucketResponseModel,
        analysisMetric: ChartMetricsEnum
    ): number {
        let res = 0;
        switch (analysisMetric) {
            case ChartMetricsEnum.volumeOfMentions:
                return bucket.count;

            case ChartMetricsEnum.ave:
                if (bucket.buckets[ReportsNeoFacetKeys.aveOverTimeFacet] && bucket.buckets[ReportsNeoFacetKeys.aveOverTimeFacet][0]?.count) {
                    res = bucket.buckets[ReportsNeoFacetKeys.aveOverTimeFacet][0].count;
                }
                break;
            case ChartMetricsEnum.IMPRESSIONS:
                if (bucket.buckets[ReportsNeoFacetKeys.impressionsOverTimeFacet] && bucket.buckets[ReportsNeoFacetKeys.impressionsOverTimeFacet][0]?.count) {
                    res = bucket.buckets[ReportsNeoFacetKeys.impressionsOverTimeFacet][0].count;
                }
                break;
            case ChartMetricsEnum.AVERAGE_DOMAIN_AUTHORITY:
                if (bucket.buckets[ReportsNeoFacetKeys.impressionsOverTimeFacet]?.[0]?.count && bucket.count) {
                    res = bucket.buckets[ReportsNeoFacetKeys.impressionsOverTimeFacet][0].count / bucket.count;
                }
                break;
            case ChartMetricsEnum.ENGAGEMENTS:
                if (bucket.buckets[ReportsNeoFacetKeys.engagementsOverTimeFacet] && bucket.buckets[ReportsNeoFacetKeys.engagementsOverTimeFacet][0]?.count) {
                    res = bucket.buckets[ReportsNeoFacetKeys.engagementsOverTimeFacet][0].count;
                }
                break;
            case ChartMetricsEnum.ENGAGEMENTS_PER_POST:
                if (bucket.buckets[ReportsNeoFacetKeys.engagementsOverTimeFacet]?.[0]?.count && bucket.count) {
                    res = bucket.buckets[ReportsNeoFacetKeys.engagementsOverTimeFacet][0].count / bucket.count;
                } else {
                    res = 0;
                }
                break;
            case ChartMetricsEnum.ENGAGEMENT_RATE:
                const followerCount = bucket.buckets[ReportsNeoFacetKeys.impressionsOverTimeFacet]?.[0]?.count || 0;
                const engagementCount = bucket.buckets[ReportsNeoFacetKeys.engagementsOverTimeFacet]?.[0]?.count;
                res = 0;
                if (followerCount > 0) {
                    res = +(engagementCount / followerCount * 100).toFixed(1);
                }
                break;
            case ChartMetricsEnum.APPLAUSE:
                if (bucket.buckets[ReportsNeoFacetKeys.engagementTotalLikes] && bucket.buckets[ReportsNeoFacetKeys.engagementTotalLikes][0]?.count) {
                    res = bucket.buckets[ReportsNeoFacetKeys.engagementTotalLikes][0].count;
                }
                break;
            case ChartMetricsEnum.CONVERSATION:
                if (bucket.buckets[ReportsNeoFacetKeys.engagementTotalQuotes] && bucket.buckets[ReportsNeoFacetKeys.engagementTotalQuotes][0]?.count){
                    res += bucket.buckets[ReportsNeoFacetKeys.engagementTotalQuotes][0].count;
                }

                if (bucket.buckets[ReportsNeoFacetKeys.engagementTotalReply] && bucket.buckets[ReportsNeoFacetKeys.engagementTotalReply][0]?.count) {
                    res += bucket.buckets[ReportsNeoFacetKeys.engagementTotalReply][0].count;
                }
                break;
            case ChartMetricsEnum.AMPLIFICATION:
                if (bucket.buckets[ReportsNeoFacetKeys.engagementTotalShare] && bucket.buckets[ReportsNeoFacetKeys.engagementTotalShare][0]?.count) {
                    res = bucket.buckets[ReportsNeoFacetKeys.engagementTotalShare][0].count;
                }
                break;
            case ChartMetricsEnum.VIEWS:
                if (bucket.buckets[ReportsNeoFacetKeys.engagementTotalView] && bucket.buckets[ReportsNeoFacetKeys.engagementTotalView][0]?.count) {
                    res = bucket.buckets[ReportsNeoFacetKeys.engagementTotalView][0].count;
                }
                break;
            default:
                return bucket.count;
        }

        return res;
    }

    private findTop10FacetField(series: ReportSeriesModel, metric: ChartMetricsEnum): MentionSearchFacetTypesEnum {
        let facetField: MentionSearchFacetTypesEnum;
        const authorMetricsToFacetsMap = new Map<ChartMetricsEnum, MentionSearchFacetTypesEnum>([
            [ ChartMetricsEnum.volumeOfMentions, MentionSearchFacetTypesEnum.contact ],
            [ ChartMetricsEnum.IMPRESSIONS, MentionSearchFacetTypesEnum.authorByAudienceReach ],
            [ ChartMetricsEnum.audienceReach, MentionSearchFacetTypesEnum.authorByAudienceReach ],
            [ ChartMetricsEnum.ave, MentionSearchFacetTypesEnum.authorByAVE ]
        ]);
        const outletMetricsToFacetsMap = new Map<ChartMetricsEnum, MentionSearchFacetTypesEnum>([
            [ ChartMetricsEnum.volumeOfMentions, MentionSearchFacetTypesEnum.outlet ],
            [ ChartMetricsEnum.IMPRESSIONS, MentionSearchFacetTypesEnum.outletByAudienceReach ],
            [ ChartMetricsEnum.audienceReach, MentionSearchFacetTypesEnum.outletByAudienceReach ],
            [ ChartMetricsEnum.ave, MentionSearchFacetTypesEnum.outletByAVE ],
            [ ChartMetricsEnum.domainAuthority, MentionSearchFacetTypesEnum.outletByDomainAuthority ]
        ]);

        switch (series.chartType) {
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
                facetField = authorMetricsToFacetsMap.get(metric) || MentionSearchFacetTypesEnum.contact;
                break;
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
                facetField = outletMetricsToFacetsMap.get(metric) || MentionSearchFacetTypesEnum.outlet;
                break;
            case ReportBlockTypeEnum.TOP_HASHTAGS:
                facetField = MentionSearchFacetTypesEnum.hashtags;
        }

        return facetField;
    }

    private getMetricValue(neoFacetValue, metric: ChartMetricsEnum): number {
        let value: number = 0;
        switch (metric) {
            case ChartMetricsEnum.volumeOfMentions:
                value = neoFacetValue.count;
                break;
            case ChartMetricsEnum.IMPRESSIONS:
            case ChartMetricsEnum.audienceReach:
                if (neoFacetValue.buckets &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeImpressions] &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeImpressions].length
                ) {
                    value = neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeImpressions][0].count || 0;
                }
                break;
            case ChartMetricsEnum.ENGAGEMENTS:
                if (neoFacetValue.buckets &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeEngagements] &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeEngagements].length
                ) {
                    value = neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeEngagements][0].count || 0;
                }
                if (neoFacetValue.buckets &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.engagementsOverTimeFacet] &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.engagementsOverTimeFacet].length
                ) {
                    value = neoFacetValue.buckets[ReportsNeoFacetKeys.engagementsOverTimeFacet][0].count || 0;
                }
                break;
            case ChartMetricsEnum.ENGAGEMENT_RATE:
                if (neoFacetValue.buckets &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeEngagements] &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeEngagements].length &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeImpressions] &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeImpressions].length
                ) {
                    if (neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeImpressions][0].count > 0) {
                        value = ((neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeEngagements][0].count || 0) /
                            neoFacetValue.buckets[ReportsNeoFacetKeys.mediaTypeImpressions][0].count) * 100;
                    }
                }
                break;
            case ChartMetricsEnum.APPLAUSE:
            case ChartMetricsEnum.LIKES:
                if (neoFacetValue.buckets &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalLikes] &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalLikes].length
                ) {
                    value = neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalLikes][0].count || 0;
                }
                break;
            case ChartMetricsEnum.CONVERSATION:
                const replyBuckets = neoFacetValue.buckets?.[ReportsNeoFacetKeys.engagementTotalReply];
                const quoteBuckets = neoFacetValue.buckets?.[ReportsNeoFacetKeys.engagementTotalQuotes];
                if ((replyBuckets && replyBuckets.length) || (quoteBuckets && quoteBuckets.length)) {
                    value = (replyBuckets?.[0]?.count || 0) + (quoteBuckets?.[0]?.count || 0);
                }
                break;
            case ChartMetricsEnum.AMPLIFICATION:
            case ChartMetricsEnum.SHARES:
                if (neoFacetValue.buckets &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalShare] &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalShare].length
                ) {
                    value = neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalShare][0].count || 0;
                }
                break;
            case ChartMetricsEnum.VIEWS:
                if (neoFacetValue.buckets &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalView] &&
                    neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalView].length
                ) {
                    value = neoFacetValue.buckets[ReportsNeoFacetKeys.engagementTotalView][0].count || 0;
                }
                break;
            default:
                // do nothing
        }

        return value;
    }


    private isValueSum(type: MentionSearchFacetTypesEnum): boolean {
        const valuesBySum: MentionSearchFacetTypesEnum[] = [
            MentionSearchFacetTypesEnum.outletByAVE,
            MentionSearchFacetTypesEnum.outletByAudienceReach,
            MentionSearchFacetTypesEnum.authorByAVE,
            MentionSearchFacetTypesEnum.authorByAudienceReach
        ];

        return valuesBySum.includes(type);
    }

    private mapXValueByFacet(facetValue: FacetValueModel, type: MentionSearchFacetTypesEnum): number {
        let value: number;

        switch (type) {
            case MentionSearchFacetTypesEnum.outletByAVE:
            case MentionSearchFacetTypesEnum.outletByAudienceReach:
            case MentionSearchFacetTypesEnum.authorByAVE:
            case MentionSearchFacetTypesEnum.authorByAudienceReach:
                value = facetValue.sum;
                break;
            case MentionSearchFacetTypesEnum.outletByDomainAuthority:
                value = parseFloat('' + facetValue.metricValue);
                break;
            default:
                value = facetValue.count;
        }

        return value;
    }

    private getSentimentValuesFromResponse(facetResults: FacetResponseModel[]): { [key in SearchCriteriaQuerySentimentsEnum]: number } {
        const results = {
            [SearchCriteriaQuerySentimentsEnum.positive]: 0,
            [SearchCriteriaQuerySentimentsEnum.neutral]: 0,
            [SearchCriteriaQuerySentimentsEnum.negative]: 0,
            [SearchCriteriaQuerySentimentsEnum.notAvailable]: 0
        };
        for (const facetType of facetResults) {
            if (facetType.field !== MentionSearchFacetTypesEnum.sentiment) {
                continue;
            }

            for (const facetValue of facetType.values) {
                const floatValue = parseFloat(facetValue.value);

                if (isNaN(floatValue)) {
                    results[SearchCriteriaQuerySentimentsEnum.notAvailable] += facetValue.count;
                    continue;
                }

                // Negative -1 to -0.3
                // Positive 0.3 to 1
                // Neutral -0.2 to 0.2
                if (floatValue <= -0.3) {
                    results[SearchCriteriaQuerySentimentsEnum.negative] += facetValue.count;
                } else if (floatValue >= 0.3) {
                    results[SearchCriteriaQuerySentimentsEnum.positive] += facetValue.count;
                } else {
                    results[SearchCriteriaQuerySentimentsEnum.neutral] += facetValue.count;
                }
            }
        }
        return results;
    }

    private generateFilterErrorMessage(error: any): string {
        if (error?.filterTypes?.some( errorType =>
            errorType === monitoringFiltersEnum.regions
            || errorType === monitoringFiltersEnum.country
            || errorType === monitoringFiltersEnum.city
            || errorType === monitoringFiltersEnum.province
        )) {
            return this.localizationService.transform('Reports.filterError.regions');
        } else {
            throw error;
        }
    }

    /**
     * Get Country subdivisions
     */
    getCountrySubdivisions(journodexId: number, geographyScale: GeographyTypesEnum): Observable<JournodexGeographyModel[]> {
        const geographyModel = new JournodexGeographyModel();
        geographyModel.id = journodexId;
        geographyModel.geographicType = geographyScale;
        geographyModel.country = <JournodexGeographicEntityModel>{ id: journodexId, name: null, names: null };

        return this.journodexSearchService.getChildRegions(geographyModel);
    }

    //#endregion
}
